import { z } from 'zod';
import { MAIL_DIRECTIONS, MAIL_STATUSES, MAIL_VIEWS } from '../constants/mail.js';
import { emailSchema, patchableSchema, uuidSchema } from './common.js';
// ------------------------------------------------------------- mailboxes
export const mailboxSchema = z.object({
    id: uuidSchema,
    address: emailSchema,
    displayName: z.string(),
    userId: uuidSchema.nullable(),
    userName: z.string().nullable(),
    signature: z.string().nullable(),
    active: z.boolean(),
    forwardingAddress: z.string().nullable(),
    forwardingEnabled: z.boolean(),
    smtpCredentialCreatedAt: z.iso.datetime().nullable(),
    unreadCount: z.number().int().nonnegative(),
});
export const createMailboxSchema = z.object({
    address: emailSchema,
    displayName: z.string().trim().min(1).max(150),
    userId: uuidSchema.nullable().default(null),
    signature: z.string().trim().max(2000).nullable().default(null),
});
/** Patchable, not partial — see {@link patchableSchema}. `active: false`
 *  must not silently blank the display name or signature. */
export const updateMailboxSchema = patchableSchema(createMailboxSchema).extend({
    active: z.boolean().optional(),
    forwardingAddress: emailSchema.nullable().optional(),
    forwardingEnabled: z.boolean().optional(),
});
/** Returned once when a mailbox's SMTP login is created or reset — the
 *  password is not stored anywhere and cannot be shown again. */
export const mailboxSmtpCredentialSchema = z.object({
    username: emailSchema,
    password: z.string(),
    smtpHost: z.string(),
    smtpPort: z.literal(587),
    createdAt: z.iso.datetime(),
});
/** `includeInactive` is honored only for callers with `mail:manage`;
 *  everyone else always gets the active-only list. */
export const listMailboxesQuerySchema = z.object({
    includeInactive: z.coerce.boolean().optional(),
});
// ------------------------------------------------------------- messages
export const mailDirectionSchema = z.enum(MAIL_DIRECTIONS);
export const mailStatusSchema = z.enum(MAIL_STATUSES);
export const mailViewSchema = z.enum(MAIL_VIEWS);
export const mailAttachmentMetaSchema = z.object({
    id: uuidSchema,
    filename: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
});
/** One row in the middle-pane conversation list — no bodies, so listing a
 *  mailbox with years of history stays cheap. */
export const conversationListItemSchema = z.object({
    threadId: z.string(),
    subject: z.string(),
    /** Every distinct from/to participant across the thread, for the list row. */
    participants: z.array(z.string()),
    snippet: z.string(),
    lastMessageId: uuidSchema,
    lastAt: z.iso.datetime(),
    messageCount: z.number().int().positive(),
    unread: z.boolean(),
    starred: z.boolean(),
    hasAttachments: z.boolean(),
    hasDraft: z.boolean(),
    lastStatus: mailStatusSchema,
});
export const mailMessageSchema = z.object({
    id: uuidSchema,
    mailboxId: uuidSchema,
    threadId: z.string(),
    direction: mailDirectionSchema,
    status: mailStatusSchema,
    fromAddress: z.string(),
    toAddresses: z.array(z.string()),
    ccAddresses: z.array(z.string()),
    bccAddresses: z.array(z.string()),
    subject: z.string(),
    textBody: z.string().nullable(),
    htmlBody: z.string().nullable(),
    inReplyTo: z.string().nullable(),
    providerMessageId: z.string().nullable(),
    errorMessage: z.string().nullable(),
    starred: z.boolean(),
    readAt: z.iso.datetime().nullable(),
    archivedAt: z.iso.datetime().nullable(),
    trashedAt: z.iso.datetime().nullable(),
    junkedAt: z.iso.datetime().nullable(),
    sentAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    /** The Support ticket this message was turned into (t1092), if any. */
    supportTicketId: uuidSchema.nullable(),
    attachments: z.array(mailAttachmentMetaSchema),
});
export const mailThreadSchema = z.object({
    threadId: z.string(),
    messages: z.array(mailMessageSchema),
});
export const mailConversationsQuerySchema = z.object({
    view: mailViewSchema.default('inbox'),
    q: z.string().trim().max(200).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
/** The folders the combined "All mail" view covers — Junk, Trash and Archive
 *  are deliberately left out. */
export const ALL_MAIL_FOLDERS = ['inbox', 'starred', 'sent', 'drafts'];
export const allMailFolderSchema = z.enum(ALL_MAIL_FOLDERS);
export const allMailConversationsQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
/** A conversation row in the combined view: which mailbox it is in, and
 *  which of the covered folders it currently shows up under. */
export const allMailConversationListItemSchema = conversationListItemSchema.extend({
    mailboxId: uuidSchema,
    mailboxName: z.string(),
    folders: z.array(allMailFolderSchema),
});
/** One row in the Attachments tab — every inbound attachment in a mailbox,
 *  independent of which folder its message currently sits in (archived and
 *  trashed messages still surface here; deleting the message is what removes
 *  its attachments, not moving it). */
export const mailAttachmentListItemSchema = z.object({
    id: uuidSchema,
    filename: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    messageId: uuidSchema,
    threadId: z.string(),
    fromAddress: z.string(),
    subject: z.string(),
    createdAt: z.iso.datetime(),
});
export const mailAttachmentsQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
/**
 * One request shape covers composing new mail, replying, forwarding, and
 * saving a draft: the fields that differ (`inReplyToMessageId`,
 * `forwardOfMessageId`) are just nullish extras, and `draft: true` stops the
 * service short of enqueueing a send. At least one of `html`/`text` must be
 * present — the route enforces that, since it depends on which is missing.
 */
export const composeMailSchema = z.object({
    mailboxId: uuidSchema,
    // No `.min(1)` here — a draft may legitimately have no recipients yet.
    // The server enforces "at least one" only for an actual send.
    to: z.array(emailSchema).max(20),
    cc: z.array(emailSchema).max(20).default([]),
    bcc: z.array(emailSchema).max(20).default([]),
    subject: z.string().trim().max(500).default(''),
    html: z.string().max(5 * 1024 * 1024).nullable().default(null),
    text: z.string().max(5 * 1024 * 1024).nullable().default(null),
    inReplyToMessageId: uuidSchema.nullish(),
    forwardOfMessageId: uuidSchema.nullish(),
    draft: z.boolean().default(false),
});
/** A draft's body/recipients can all change on autosave; `to` stays required
 *  everywhere else, but an in-progress draft may legitimately have none yet. */
export const updateDraftSchema = composeMailSchema
    .omit({ mailboxId: true, inReplyToMessageId: true, forwardOfMessageId: true, draft: true })
    .partial()
    .extend({ to: z.array(emailSchema).max(20).optional() });
export const messageFlagsSchema = z.object({
    starred: z.boolean().optional(),
    read: z.boolean().optional(),
    archived: z.boolean().optional(),
    trashed: z.boolean().optional(),
    junked: z.boolean().optional(),
});
export const bulkMailActionSchema = z.object({
    threadIds: z.array(z.string()).min(1).max(200),
    action: z.enum(['read', 'unread', 'archive', 'unarchive', 'trash', 'untrash', 'junk', 'unjunk', 'inbox', 'star', 'unstar']),
});
//# sourceMappingURL=mail.js.map